import {
  useMutation as _useMutation,
  UseMutationOptions,
  UseMutationResult,
  MutationFunction,
  DefaultError,
} from '@tanstack/react-query';

export type UseMutationOptionsWithCallbacks<
  TData,
  TError,
  TVariables,
  TContext,
> = UseMutationOptions<TData, TError, TVariables, TContext>;

export type UseMutationResultWithIsLoading<
  TData,
  TError,
  TVariables,
  TContext,
> = UseMutationResult<TData, TError, TVariables, TContext> & {
  isLoading: boolean;
};

function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: Omit<
    UseMutationOptionsWithCallbacks<TData, TError, TVariables, TContext>,
    'mutationFn'
  >,
): UseMutationResultWithIsLoading<TData, TError, TVariables, TContext>;

function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptionsWithCallbacks<TData, TError, TVariables, TContext>,
): UseMutationResultWithIsLoading<TData, TError, TVariables, TContext>;

function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  arg1: any,
  arg2?: any,
): UseMutationResultWithIsLoading<TData, TError, TVariables, TContext> {
  const options: UseMutationOptions<TData, TError, TVariables, TContext> =
    typeof arg1 === 'function' ? { ...arg2, mutationFn: arg1 } : { ...arg1 };

  const mutationResult = _useMutation(options);

  // Add isLoading alias for backward compatibility (v3 used isLoading, v5 uses isPending)
  return {
    ...mutationResult,
    isLoading: mutationResult.isPending,
  };
}

export default useMutation;
